import React, { useContext, useEffect, useMemo, useState } from "react";
import { Box, Grid } from "@mui/material";
import { AuthContext } from "../../context/AuthContext";
import { WISH_LIST } from "../../services/apiRoutes";
import { genericPost } from "../../services/authServices";
import CustomLoader from "../../components/common/CustomLoader";
import CustomDataTable from "../../components/common/CustomDataTable";
import CustomFilter from "../../components/common/CustomFilter";
import CustomTextField from "../../components/common/CustomTextField";
import CustomButton from "../../components/common/CustomButton";
import FindReplaceOutlinedIcon from '@mui/icons-material/FindReplaceOutlined';
import { ClearRounded } from "@mui/icons-material";
import { getCountries } from "../../services/CommonServices";
import CustomAutocomplete from "../../components/common/CustomAutoComplete";
import CustomStatus from "../../components/common/CustomStatus";

const title = "Wish List";

const WishList = () => {
    const { token } = useContext(AuthContext);
    const [clientCompanies, setClientCompanies] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [openDrawer, setOpenDrawer] = useState(false);
    const filterOpen = () => setOpenDrawer(true);
    const filterClose = () => setOpenDrawer(false);

    // ✅ Pagination states
    const [page, setPage] = useState(0); // UI 0-based
    const [pageSize, setPageSize] = useState(10);
    const [rowCount, setRowCount] = useState(0);
    const [conutries, setConutries] = useState([]);

    const getCommonData = async () => {
        const getConutriesData = await getCountries()
        setConutries(getConutriesData)
    }

    // ✅ Filter state
    const [filterPayload, setFilterPayload] = useState({
        companyName: '',
        mobile: '',
        country: '',
        wishListType: '',
        fromDate: '',      // ✅ added
        toDate: ''         // ✅ added

    });

    // ✅ Handle filter input change
    const handleFilterChange = (field, value) => {
        setFilterPayload((prev) => ({
            ...prev,
            [field]: value || '',
        }));
    };

    // ✅ Clear filter + reload list
    const handleClearFilters = useMemo(() => {
        return () => {
            setFilterPayload({
                companyName: '',
                mobile: '',
                country: '',
                wishListType: '',
                fromDate: '',   // ✅ reset
                toDate: ''      // ✅ reset

            });
            fetchWishList(0, pageSize, '');
        };
    }, [pageSize]);

    // ✅ Fetch on pagination or pageSize change
    useEffect(() => {
        fetchWishList(page, pageSize,);
        getCommonData()
    }, [page, pageSize, token]);

    const wishTypeColorMap = {
        0: {
            label: "Enquiry",
            backgroundColor: "#E3F2FD",
            color: "#1976D2",
        },
        1: {
            label: "Partner",
            backgroundColor: "#E8F5E9",
            color: "#2E7D32",
        },
        2: {
            label: "Book Now",
            backgroundColor: "#FFF3E0", // light orange
            color: "#EF6C00", // orange
        }
    };

    const columns = useMemo(
        () => [
            { accessorKey: "index", header: "SN.", size: 50 },
            { accessorKey: "firstName", header: "First Name", size: 150 },
            { accessorKey: "lastName", header: "Last Name", size: 150 },
            { accessorKey: "companyName", header: "Company", size: 200 },
            { accessorKey: "country", header: "Country", size: 150 },
            { accessorKey: "mobile", header: "Contact No.", size: 150 },
            { accessorKey: "email", header: "Email ID", size: 250 },
            // { accessorKey: "wishType", header: "Wish Type", size: 150 },
            {
                accessorKey: "wishType",
                header: "Wish Type",
                size: 100,
                Cell: ({ row }) => (
                    <>
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1px' }}>
                            <CustomStatus
                                title={row.original.wishType}
                                sx={{
                                    backgroundColor: row.original.backgroundColor,
                                    color: row.original.color,
                                    textAlign: "center",
                                    display: "inline-block",
                                }}
                            />
                        </div>

                    </>
                ),
                enableSorting: false
            },


        ],
        []
    );

    const fetchWishList = async (pageIndex = 0, pageSize = 10,) => {
        setLoading(true);
        try {
            const payload = {
                page: pageIndex + 1,
                limit: pageSize,
                ...filterPayload
            };

            const response = await genericPost(WISH_LIST, payload);

            if (response?.data?.success) {
                const wishData = response.data.data.map((item, index) => ({
                    index: (index + 1).toString().padStart(2, '0'),
                    wishId: item?.wishId ?? "",
                    firstName: item?.firstName ?? "",
                    lastName: item?.lastName ?? "",
                    companyName: item?.companyName ?? "",
                    country: item?.countryList?.name ?? item?.country ?? "",
                    email: item?.email ?? "",
                    mobile: item?.mobile ?? "",
                    wishType:
                        item?.wishListType === 0
                            ? "Enquiry"
                            : item?.wishListType === 1
                                ? "Partner"
                                : item?.wishListType === 2
                                    ? "Book Now"
                                    : "N/A",
                    backgroundColor: wishTypeColorMap[item?.wishListType]?.backgroundColor || "#E0E0E0",
                    color: wishTypeColorMap[item?.wishListType]?.color || "#000000",

                }));

                setClientCompanies(wishData);
                setRowCount(response.data.pagination?.totalRecords || 0);
            } else {
                setClientCompanies([]);
                setRowCount(0);
                setError("No wish records found");
            }
        } catch (error) {
            console.error("Error fetching wishes:", error);
            setError("Error fetching wish data");
        } finally {
            setLoading(false);
        }
    };

    const wishTypeOptions = [
        { label: "Enquiry Wish", value: '0' },
        { label: "Partner Wish", value: '1' },
        { label: "Book Now", value: '2' },
    ];

    return (
        <>
            <Box sx={{ padding: "10px" }}>
                <CustomLoader open={loading} />
                <CustomDataTable
                    title={title}
                    columns={columns}
                    data={clientCompanies}
                    handleFilterButton={filterOpen}
                    fiterButtonTitile="Filter"
                    rowCount={rowCount}
                    page={page}
                    setPage={setPage}
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                />
            </Box>

            {/* ✅ Filter Drawer */}
            <CustomFilter
                drawerTitle="Filter Options"
                drawerWidth={300}
                openDrawer={openDrawer}
                onClose={filterClose}
                drawerContent={
                    <Box>
                        <Grid container spacing={1} sx={{ padding: 0 }}>
                            <Grid item xs={12}>
                                <CustomTextField
                                    type="text"
                                    placeholder="Enter Company Name"
                                    value={filterPayload.companyName}
                                    onChange={(e) => handleFilterChange('companyName', e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={12} sm={12} md={12}>
                                <CustomAutocomplete
                                    options={conutries}
                                    getOptionLabel={(option) => option.title}
                                    placeholder="Select Country"
                                    onChange={(event, newValue) => handleFilterChange("country", newValue?.id)}
                                    value={conutries.find((item) => item.id === filterPayload.country) || null}

                                />
                            </Grid>
                            <Grid item xs={12}>
                                <CustomAutocomplete
                                    options={wishTypeOptions}
                                    getOptionLabel={(option) => option.label}
                                    placeholder="Select Wish Type"
                                    onChange={(event, newValue) =>
                                        handleFilterChange("wishListType", newValue?.value ?? '')
                                    }
                                    value={
                                        wishTypeOptions.find(
                                            (item) => item.value === filterPayload.wishListType
                                        ) || null
                                    }
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <CustomTextField
                                    type="number"
                                    placeholder="Enter Mobile Number"
                                    value={filterPayload.mobile}
                                    onChange={(e) => handleFilterChange('mobile', e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <CustomTextField
                                    fieldLabel="From Date"
                                    type="date"
                                    value={filterPayload.fromDate}
                                    onChange={(e) => handleFilterChange('fromDate', e.target.value)}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <CustomTextField
                                    fieldLabel="To Date"
                                    type="date"
                                    value={filterPayload.toDate}
                                    onChange={(e) => handleFilterChange('toDate', e.target.value)}
                                />
                            </Grid>

                            <Grid
                                item
                                xs={12}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    marginTop: "10px",
                                }}
                            >
                                <CustomButton
                                    startIcon={<FindReplaceOutlinedIcon />}
                                    title="Apply"
                                    size="small"
                                    variant="contained"
                                    onClick={fetchWishList}
                                />
                                <CustomButton
                                    variant="outlined"
                                    title="Clear"
                                    startIcon={<ClearRounded />}
                                    onClick={handleClearFilters}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                }
            />
        </>
    );
};

export default WishList;
